// The dashboard frame shared by the admin area and the member area (adapted from davinci-elections
// apps/web/src/layouts/OrganizerShell.tsx @ 6e23f58, itself mirrored from vocdoni-app src/elements/DashboardShell.tsx):
// a 255 px sidebar on the chrome surface (sticky, full height) with the pages and a footer slot at the bottom. Below the
// `sidebarFrom` breakpoint a slim top bar takes its place, and the pages are either in a drawer the top bar opens
// (`mobileNav='drawer'`, the admin area, from 768 px) or in a bottom tab bar (`mobileNav='tabs'`, the member area, from
// 1024 px: mobile review, ruling M3). R113: the frame (sidebar, top bar, tab bar) is the recessed `bg.chrome` surface with a
// `border.chrome` edge, the page is the `bg.canvas` behind the content cards, and the current page is marked by a raised
// content-surface tile with an accent bar and the accent label.
import {
  Badge,
  Box,
  CloseButton,
  Drawer,
  Flex,
  Icon,
  IconButton,
  Portal,
  Stack,
  VisuallyHidden,
} from '@chakra-ui/react'
import { useEffect, useState, type ElementType, type ReactNode } from 'react'
import { LuPanelLeft } from 'react-icons/lu'
import { NavLink, Outlet, useLocation } from 'react-router'
import { SkipLink } from '~/components/site'
import { SHORT, visuallyHidden } from '~/theme/media'

export interface ShellNavItem {
  to: string
  label: string
  icon: ElementType
  /** Match the path exactly (the section index). */
  end?: boolean
  /** A shorter label for the bottom tab bar (defaults to `label`); it is also the tab's accessible name. */
  short?: string
  /** A count shown after the label (the admin review queue); `badgeLabel` is what a screen reader hears for it. */
  badge?: number
  badgeLabel?: string
}

/**
 * The bottom tab bar of a phone or a tablet (`mobileNav='tabs'`): every page one tap away, an icon over a short label,
 * at least 56 px tall, clear of the home indicator, the current page marked (`aria-current="page"` and the brand
 * colour). When the bar is too narrow for the labels at the current text size (a 320 px phone, or larger text) the
 * labels are kept for screen readers only and the icons stay.
 */
function TabBar({ items, navLabel, from }: { items: ShellNavItem[]; navLabel: string; from: 'md' | 'lg' }) {
  return (
    <Box
      as='nav'
      aria-label={navLabel}
      hideFrom={from}
      position='fixed'
      insetX={0}
      bottom={0}
      zIndex='sticky'
      bg='bg.chrome'
      borderTop='1px solid'
      borderColor='border.chrome'
      boxShadow='dockBottom'
      pb='env(safe-area-inset-bottom, 0px)'
      css={{ containerType: 'inline-size' }}
    >
      <Flex as='ul' listStyleType='none' m={0} px={1} py={0} maxW='40rem' mx='auto'>
        {items.map((item) => (
          <Box as='li' key={item.to} flex='1 1 auto' minW={0}>
            <NavLink
              to={item.to}
              end={item.end}
              style={{ display: 'block', height: '100%' }}
              // The name is the short label shown (WCAG 2.5.3); the full page name is the tooltip.
              title={item.short && item.short !== item.label ? item.label : undefined}
            >
              {({ isActive }) => (
                <Flex
                  as='span'
                  direction='column'
                  align='center'
                  justify='center'
                  gap={0.5}
                  minH={14}
                  h='full'
                  px={1}
                  textAlign='center'
                  color={isActive ? 'brand.fg' : 'fg.muted'}
                  fontWeight={isActive ? 'bold' : 'normal'}
                  borderTop='3px solid'
                  // `brand.border`: the accent step that reaches 3:1 on every surface, in both modes.
                  borderColor={isActive ? 'brand.border' : 'transparent'}
                  // The raised tile: `bg.panel` (white in light mode, a clear step above the dark chrome).
                  bg={isActive ? 'bg.panel' : undefined}
                  // A phone in landscape: icon beside the label, a 48 px bar instead of 56.
                  css={{ [SHORT]: { flexDirection: 'row', minHeight: '12', gap: '2' } }}
                  _hover={{ color: isActive ? 'brand.fg' : 'fg', bg: isActive ? 'bg.panel' : 'bg.chrome.hover' }}
                >
                  <Icon as={item.icon} boxSize={5} aria-hidden flexShrink={0} />
                  <Box
                    as='span'
                    lineHeight='1.25'
                    whiteSpace='nowrap'
                    // The five short labels need about 21.5 rem side by side; under that (a 320 px phone, larger text) icons only.
                    css={{ '@container (max-width: 21.5rem)': visuallyHidden }}
                  >
                    {item.short ?? item.label}
                  </Box>
                </Flex>
              )}
            </NavLink>
          </Box>
        ))}
      </Flex>
    </Box>
  )
}

/**
 * One menu item: icon and label. The current page is a raised tile on the chrome (`bg.panel`, an accent bar on its
 * leading edge, the accent label at 500 weight); the others are plain text (muted in dark mode, so eleven light labels
 * do not compete with the page title) that gets the chrome's hover fill.
 */
export function ShellNavItemLink({ item, touch = false }: { item: ShellNavItem; touch?: boolean }) {
  return (
    <NavLink to={item.to} end={item.end} style={{ display: 'block', borderRadius: 6 }}>
      {({ isActive }) => (
        <Flex
          as='span'
          align='center'
          gap={3}
          px={2}
          minH={touch ? 12 : 9}
          borderRadius='sm'
          fontSize='sm'
          color={isActive ? 'brand.fg' : { base: 'fg', _dark: 'fg.muted' }}
          fontWeight={isActive ? 'bold' : undefined}
          bg={isActive ? 'bg.panel' : undefined}
          boxShadow={
            isActive ? 'inset 3px 0 0 0 var(--chakra-colors-brand-border), var(--chakra-shadows-surface)' : undefined
          }
          _hover={{ bg: isActive ? 'bg.panel' : 'bg.chrome.hover', color: isActive ? 'brand.fg' : 'fg' }}
        >
          <Icon as={item.icon} boxSize={4} aria-hidden color={isActive ? 'brand.fg' : 'fg.muted'} />
          {item.label}
          {item.badge !== undefined && item.badge > 0 && (
            <>
              <Badge ms='auto' colorPalette='brand' variant='solid' borderRadius='full' aria-hidden>
                {item.badge}
              </Badge>
              <VisuallyHidden>{item.badgeLabel ?? String(item.badge)}</VisuallyHidden>
            </>
          )}
        </Flex>
      )}
    </NavLink>
  )
}

/** The sidebar contents: `header`, the pages, then `footer` pinned to the bottom. */
function Menu({
  header,
  items,
  navLabel,
  footer,
  touch,
}: {
  header: ReactNode
  items: ShellNavItem[]
  navLabel: string
  footer: ReactNode
  touch: boolean
}) {
  return (
    <Flex direction='column' h='full' gap={6}>
      {header}
      <Box as='nav' aria-label={navLabel}>
        <Stack as='ul' gap={0.5} listStyleType='none' m={0} p={0}>
          {items.map((item) => (
            <li key={item.to}>
              <ShellNavItemLink item={item} touch={touch} />
            </li>
          ))}
        </Stack>
      </Box>
      <Box mt='auto'>{footer}</Box>
    </Flex>
  )
}

/**
 * The frame of a dashboard area. The page renders in `<main id="main">` through the router `Outlet`.
 * `mobileBar` is what the slim top bar shows after the menu button (the wordmark and the theme control).
 */
export function DashboardShell({
  header,
  items,
  navLabel,
  openMenuLabel,
  footer,
  mobileBar,
  mainFooter,
  touch = false,
  sidebarFrom = 'md',
  mobileNav = 'drawer',
}: {
  header: ReactNode
  items: ShellNavItem[]
  navLabel: string
  openMenuLabel: string
  footer: ReactNode
  mobileBar: ReactNode
  /** Rendered right under the page content, inside `<main>` (the member area's Help button). */
  mainFooter?: ReactNode
  /** Big touch targets (at least 48 px) for the member area; the admin keeps the compact sizes. */
  touch?: boolean
  /** The width from which the sidebar shows (`md` 768 px, the admin; `lg` 1024 px, the member area). */
  sidebarFrom?: 'md' | 'lg'
  /** Below `sidebarFrom`: a menu button that opens the menu in a drawer, or a bottom tab bar with every page. */
  mobileNav?: 'drawer' | 'tabs'
}) {
  const [open, setOpen] = useState(false)
  const { pathname } = useLocation()
  // Close the mobile drawer after navigating.
  useEffect(() => setOpen(false), [pathname])
  const menu = <Menu header={header} items={items} navLabel={navLabel} footer={footer} touch={touch} />
  // A single page (the "not accepted" card) needs no tab bar.
  const tabs = mobileNav === 'tabs' && items.length > 1
  return (
    <Flex minH='100dvh' w='full' bg='bg.canvas' position='relative'>
      <SkipLink />
      <Box
        as='aside'
        display='block'
        hideBelow={sidebarFrom}
        alignSelf='stretch'
        minH='100dvh'
        w='dashboard-menu.default'
        flexShrink={0}
        bg='bg.chrome'
        borderRight='1px solid'
        borderColor='border.chrome'
      >
        {/* The column runs the full height of the page; the menu inside it stays in view while the page scrolls. */}
        <Box position='sticky' top={0} h='100dvh' overflowY='auto' p={3} display='flex' flexDirection='column'>
          {menu}
        </Box>
      </Box>

      {mobileNav === 'drawer' && (
        <Drawer.Root open={open} placement='start' onOpenChange={(e) => setOpen(e.open)} lazyMount unmountOnExit>
          <Portal>
            <Drawer.Backdrop />
            <Drawer.Positioner>
              <Drawer.Content maxW='dashboard-menu.default' p={3}>
                <Drawer.CloseTrigger asChild position='absolute' top={2} insetEnd={2}>
                  <CloseButton size='sm' minW={touch ? 12 : undefined} minH={touch ? 12 : undefined} />
                </Drawer.CloseTrigger>
                {menu}
              </Drawer.Content>
            </Drawer.Positioner>
          </Portal>
        </Drawer.Root>
      )}

      <Flex direction='column' flex='1' minW={0}>
        <Flex
          hideFrom={sidebarFrom}
          align='center'
          gap={mobileNav === 'tabs' ? 1 : 3}
          px={4}
          py={mobileNav === 'tabs' ? 1 : 2}
          bg='bg.chrome'
          borderBottom='1px solid'
          borderColor='border.chrome'
        >
          {mobileNav === 'drawer' && (
            <IconButton
              aria-label={openMenuLabel}
              variant='outline'
              colorPalette='gray'
              size='sm'
              minW={touch ? 12 : undefined}
              minH={touch ? 12 : undefined}
              onClick={() => setOpen(true)}
            >
              <LuPanelLeft />
            </IconButton>
          )}
          {mobileBar}
        </Flex>
        <Box
          as='main'
          id='main'
          flex='1'
          w='full'
          maxW='max-content-width'
          mx='auto'
          p={{ base: 4, md: 6 }}
          // Room for the tab bar (56 px and the home indicator), so it never covers the end of the page.
          pb={tabs ? { base: 'calc(5rem + env(safe-area-inset-bottom, 0px))', [sidebarFrom]: 6 } : undefined}
        >
          <Stack gap={6}>
            <Outlet />
            {mainFooter}
          </Stack>
        </Box>
      </Flex>
      {tabs && <TabBar items={items} navLabel={navLabel} from={sidebarFrom} />}
    </Flex>
  )
}
